import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getAgent } from '../../../api/agents';
import { listSubAgents } from '../../../api/subAgents';
import { getApiErrorMessage } from '../../../utils/errors';
import {
  EXECUTION_PHASE_LABELS,
  SUB_AGENT_TYPE_OPTIONS,
} from '../../Onboarding/constants';

const PER_PAGE = 20;

const TYPE_LABELS = Object.fromEntries(
  SUB_AGENT_TYPE_OPTIONS.map((option) => [option.value, option.label])
);

function TypeBadge({ type }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200 whitespace-nowrap">
      {TYPE_LABELS[type] || type}
    </span>
  );
}

function EnabledBadge({ enabled }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${
        enabled
          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
          : 'bg-slate-100 text-slate-600 border border-slate-200'
      }`}
    >
      {enabled ? 'Ativo' : 'Inativo'}
    </span>
  );
}

function DescriptionLine({ description }) {
  const text = description?.trim() ? description.trim() : 'Gerando...';
  const isPending = !description?.trim();

  return (
    <p
      className={`text-xs mt-0.5 truncate max-w-md ${
        isPending ? 'text-content-subtle italic' : 'text-content-muted'
      }`}
      title={isPending ? undefined : text}
    >
      {text}
    </p>
  );
}

function executionSummary(subAgent) {
  const phase = EXECUTION_PHASE_LABELS[subAgent.execution_phase] || subAgent.execution_phase;
  return `${phase} · P${subAgent.priority}`;
}

function SubAgentsList() {
  const { agentId } = useParams();
  const [agent, setAgent] = useState(null);
  const [subAgents, setSubAgents] = useState([]);
  const [meta, setMeta] = useState({ page: 1, per_page: PER_PAGE, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadPage = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError('');

      try {
        const [agentData, listResponse] = await Promise.all([
          getAgent(agentId),
          listSubAgents(agentId, { page, perPage: PER_PAGE }),
        ]);
        setAgent(agentData);
        setSubAgents(listResponse.data || []);
        setMeta(listResponse.meta || { page, per_page: PER_PAGE, total: 0 });
      } catch (err) {
        setError(getApiErrorMessage(err, 'Não foi possível carregar os comportamentos.'));
        setSubAgents([]);
      } finally {
        setLoading(false);
      }
    },
    [agentId]
  );

  useEffect(() => {
    loadPage(1);
  }, [loadPage]);

  const totalPages = Math.max(1, Math.ceil(meta.total / meta.per_page));
  const hasPagination = meta.total > meta.per_page;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <Link
            to="/agents"
            className="text-sm text-forge-700 hover:text-forge-800 font-medium"
          >
            ← Voltar aos agentes
          </Link>
          <h1 className="text-2xl font-bold text-content-primary mt-2">
            Comportamentos
          </h1>
          <p className="text-content-muted text-sm mt-1">
            {agent?.name
              ? `Sub-agentes do agente ${agent.name}.`
              : 'Sub-agentes configurados para este agente.'}
          </p>
        </div>
        <Link
          to={`/agents/${agentId}/edit`}
          className="btn-primary text-sm px-4 py-2.5 shrink-0"
        >
          Editar agente
        </Link>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-red-700">{error}</p>
          <button
            type="button"
            onClick={() => loadPage(meta.page)}
            className="btn-secondary text-sm px-3 py-2"
          >
            Tentar novamente
          </button>
        </div>
      )}

      {loading ? (
        <div className="surface-card px-4 py-12 text-center">
          <p className="text-content-muted text-sm">Carregando comportamentos...</p>
        </div>
      ) : subAgents.length === 0 && !error ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-12 text-center">
          <p className="text-content-primary font-medium">Nenhum comportamento configurado</p>
          <p className="text-content-muted text-sm mt-2 mb-6">
            Adicione sub-agentes na edição do agente para definir onboarding, skills e regras.
          </p>
          <Link
            to={`/agents/${agentId}/edit`}
            className="btn-primary text-sm px-4 py-2.5"
          >
            Editar agente
          </Link>
        </div>
      ) : (
        <>
          <div className="hidden md:block surface-card overflow-hidden">
            <table className="w-full text-sm table-fixed">
              <thead>
                <tr className="border-b border-surface-border bg-slate-50 text-left">
                  <th className="px-4 py-3 font-medium text-content-muted w-[45%]">Nome</th>
                  <th className="px-4 py-3 font-medium text-content-muted w-[20%]">Tipo</th>
                  <th className="px-4 py-3 font-medium text-content-muted w-[25%]">Execução</th>
                  <th className="px-4 py-3 font-medium text-content-muted w-[10%]">Status</th>
                </tr>
              </thead>
              <tbody>
                {subAgents.map((subAgent) => (
                  <tr
                    key={subAgent.id}
                    className="border-b border-surface-border last:border-b-0 hover:bg-slate-50/80 transition-colors"
                  >
                    <td className="px-4 py-3 min-w-0">
                      <p
                        className="font-medium text-content-primary truncate"
                        title={subAgent.name}
                      >
                        {subAgent.name}
                      </p>
                      <DescriptionLine description={subAgent.description} />
                    </td>
                    <td className="px-4 py-3">
                      <TypeBadge type={subAgent.type} />
                    </td>
                    <td className="px-4 py-3 text-content-secondary whitespace-nowrap">
                      {executionSummary(subAgent)}
                    </td>
                    <td className="px-4 py-3">
                      <EnabledBadge enabled={subAgent.enabled} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden space-y-3">
            {subAgents.map((subAgent) => (
              <div key={subAgent.id} className="surface-card p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-content-primary truncate">{subAgent.name}</p>
                    <DescriptionLine description={subAgent.description} />
                  </div>
                  <EnabledBadge enabled={subAgent.enabled} />
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-content-muted">
                  <TypeBadge type={subAgent.type} />
                  <span className="whitespace-nowrap">{executionSummary(subAgent)}</span>
                </div>
              </div>
            ))}
          </div>

          {hasPagination && (
            <div className="flex items-center justify-between gap-3 pt-2">
              <p className="text-xs text-content-subtle">
                {meta.total} comportamento{meta.total !== 1 ? 's' : ''} · página {meta.page} de{' '}
                {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={meta.page <= 1 || loading}
                  onClick={() => loadPage(meta.page - 1)}
                  className="btn-secondary text-xs px-3 py-2 disabled:opacity-40"
                >
                  Anterior
                </button>
                <button
                  type="button"
                  disabled={meta.page >= totalPages || loading}
                  onClick={() => loadPage(meta.page + 1)}
                  className="btn-secondary text-xs px-3 py-2 disabled:opacity-40"
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default SubAgentsList;
